import React, { useRef, useState, useEffect } from 'react';
import { Box, Button, HStack, Icon } from '@chakra-ui/react';
import { MdAutoAwesome, MdFileDownload } from 'react-icons/md';

import { useUser } from '../Context/userContext';
import { usePivotState } from '../hooks/usePivotState';
import { TransactionsExplorer } from '../components/dashboard/TransactionsExplorer';
import { FilterBar } from '../components/transactions-v2';
import {
  pivotModeToGroupBy,
  viewModeToExplorer,
  areSameAccountSelection,
  type AccountSelectionPhase,
} from '../components/transactions-v2/pivotMappings';

// ── component ──────────────────────────────────────────────────────────────────

export const TenantTransactionsV2Page: React.FC = () => {
  const { selectedTenant, setSelectedAccountId } = useUser();
  const [pivotState, pivotActions] = usePivotState();
  const [accountSelectionPhase, setAccountSelectionPhase] = useState<AccountSelectionPhase>('idle');
  const [isExplorerLoading, setIsExplorerLoading] = useState(false);

  const aiOpenRef = useRef<(() => void) | undefined>();
  const exportRef = useRef<(() => void) | undefined>();

  const {
    startDate, endDate, accountIds, viewMode,
    pivotMode, pivotOrientation,
    searchQuery, searchMode, filterMode, flowFilter,
  } = pivotState;

  useEffect(() => {
    if (accountSelectionPhase === 'pending' && isExplorerLoading) {
      setAccountSelectionPhase('loading');
    } else if (accountSelectionPhase === 'loading' && !isExplorerLoading) {
      setAccountSelectionPhase('idle');
    }
  }, [accountSelectionPhase, isExplorerLoading]);

  const handleAccountsChange = (ids: string[]) => {
    const currentIds = accountIds ?? [];
    if (!areSameAccountSelection(ids, currentIds)) {
      setAccountSelectionPhase('pending');
    }
    pivotActions.setAccountIds(ids);
    if (ids.length === 1) setSelectedAccountId(ids[0]);
    else setSelectedAccountId('');
  };

  if (!selectedTenant) return null;

  const groupBy = pivotModeToGroupBy(pivotMode);
  const explorerViewMode = viewModeToExplorer(viewMode, filterMode);
  // Only the plain table view gets the contained-scroll/sticky-header
  // treatment — the other views (pivot/flow/balance) keep managing their
  // own height exactly as before (see TransactionTable's containedScroll
  // prop doc and docs/plans/UI_LAYOUT_DENSITY_PLAN.md §5.4).
  const isTableView = explorerViewMode === 'table';

  return (
    <Box flex="1" minH={0} display="flex" flexDirection="column" w="full">
      <FilterBar
        startDate={startDate}
        endDate={endDate}
        onRangeSelect={pivotActions.setDateRange}
        onPreviousPeriod={pivotActions.goToPreviousPeriod}
        onNextPeriod={pivotActions.goToNextPeriod}
        selectedAccountIds={accountIds}
        onAccountsChange={handleAccountsChange}
        isAccountSelectionLoading={accountSelectionPhase !== 'idle'}
        viewMode={viewMode}
        onViewModeChange={pivotActions.setViewMode}
        pivotMode={pivotMode}
        onPivotModeChange={pivotActions.setPivotMode}
        pivotOrientation={pivotOrientation}
        onPivotOrientationChange={pivotActions.setPivotOrientation}
        searchQuery={searchQuery}
        onSearchQueryChange={pivotActions.setSearchQuery}
        searchMode={searchMode}
        onSearchModeChange={pivotActions.setSearchMode}
        filterMode={filterMode}
        onFilterModeChange={pivotActions.setFilterMode}
        flowFilter={flowFilter}
        onFlowFilterChange={pivotActions.setFlowFilter}
        actions={
          // Wraps for the same reason FilterBar's rows do — see the note there.
          <HStack spacing={2} rowGap={2} flexWrap="wrap">
            <Button
              leftIcon={<Icon as={MdAutoAwesome} boxSize={4} />}
              size="sm"
              colorScheme="purple"
              onClick={() => aiOpenRef.current?.()}
            >
              AI Analysis
            </Button>
            <Button
              leftIcon={<Icon as={MdFileDownload} boxSize={4} color="#06B6D4" />}
              size="sm"
              variant="outline"
              color="#06B6D4"
              borderColor="#06B6D4"
              _hover={{ bg: 'rgba(6, 182, 212, 0.08)' }}
              onClick={() => exportRef.current?.()}
            >
              Export
            </Button>
          </HStack>
        }
      />

      <Box
        flex="1"
        minH={0}
        display="flex"
        flexDirection="column"
        overflow={isTableView ? 'hidden' : 'auto'}
        px={2}
        py={3}
      >
        <TransactionsExplorer
          tenantId={selectedTenant.id}
          forcedDataSourceMode="ORIGINAL"
          hideControls
          controlledStartDate={startDate}
          controlledEndDate={endDate}
          controlledViewMode={explorerViewMode}
          controlledGroupBy={groupBy}
          accountIds={accountIds}
          onAccountIdsChange={handleAccountsChange}
          filterMode={filterMode}
          controlledFlowFilter={flowFilter}
          controlledSearchQuery={searchQuery}
          controlledSearchMode={searchMode}
          pivotOrientation={pivotOrientation}
          hideFlowView={false}
          onAiOpenRef={aiOpenRef}
          onExportRef={exportRef}
          onDataLoadingChange={setIsExplorerLoading}
          containedScroll={isTableView}
        />
      </Box>
    </Box>
  );
};

export default TenantTransactionsV2Page;
